/**
 * 秒の表示・入力を「分:秒:フレーム」で扱う (例: 24fps で 3.5 秒 → "00:03:12")。
 *
 * 保存形は秒 (小数 2 桁) のまま変えない。UI の表示と入力だけをフレームの目盛りに揃える。
 * fps はタイムラインの fps (project.output.fps) を使う。
 */

/** 秒を最も近いフレーム数に丸める。 */
export function secToFrames(sec: number, fps: number): number {
  return Math.round(sec * fps + 1e-6);
}

/** 秒をフレームの目盛りに吸着させ、保存形 (小数 2 桁) に丸める。 */
export function snapSecToFrame(sec: number, fps: number): number {
  return Math.round((secToFrames(sec, fps) / fps) * 100) / 100;
}

/**
 * 秒を "MM:SS:FF" (分:秒:フレーム) で表す。SMPTE タイムコード (HH:MM:SS:FF) の時を省いた形で、
 * 区切りを常に 3 つにして「分:秒」と読み違えないようにする。負の値は先頭に "-" を付ける。
 * 例 (24fps): 5.708 → "00:05:17"、3.5 → "00:03:12"、75 → "01:15:00"。
 */
export function formatTimecode(sec: number, fps: number): string {
  if (!Number.isFinite(sec) || !(fps > 0)) return '00:00:00';
  const sign = sec < 0 ? '-' : '';
  const totalFrames = secToFrames(Math.abs(sec), fps);
  const framesPerSec = Math.round(fps);
  const totalSec = Math.floor(totalFrames / framesPerSec);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${sign}${pad(Math.floor(totalSec / 60))}:${pad(totalSec % 60)}:${pad(totalFrames % framesPerSec)}`;
}

/**
 * 入力文字列を秒に直す。"MM:SS:FF" / "SS:FF" はフレーム表記、":" を含まない数値は秒として読む
 * ("3.5" → 3.5 秒)。フレームが fps 以上でも繰り上げて受け付ける ("02:30" → 3 秒 6 フレーム)。
 * 読めなければ null。
 */
export function parseTimecode(text: string, fps: number): number | null {
  const trimmed = text.trim().replace(/：/g, ':');
  if (trimmed === '' || !(fps > 0)) return null;
  if (!trimmed.includes(':')) {
    const sec = Number(trimmed);
    return Number.isFinite(sec) ? sec : null;
  }
  const parts = trimmed.split(':');
  if (parts.length > 3 || parts.some((part) => !/^\d+$/.test(part))) return null;
  const [frames, seconds, minutes = 0] = parts.map(Number).reverse();
  return (minutes! * 60 + seconds!) + frames! / fps;
}

/** フレーム数を長さの表記 "S:FF" (60 秒以上は "M:SS:FF") にする。 */
function framesToDurationLabel(totalFrames: number, fps: number): string {
  const framesPerSec = Math.round(fps);
  const totalSec = Math.floor(totalFrames / framesPerSec);
  const frames = String(totalFrames % framesPerSec).padStart(2, '0');
  if (totalSec < 60) return `${totalSec}:${frames}`;
  return `${Math.floor(totalSec / 60)}:${String(totalSec % 60).padStart(2, '0')}:${frames}`;
}

/**
 * 長さ (秒) を文言用に「秒:フレーム」で表す (Premiere / Final Cut のデュレーションと同じ読み方)。
 * 位置 (何秒目か) ではなく長さに使う。位置は formatTimecode ("MM:SS:FF") で表す。
 * 例 (24fps): 0.5 → "0:12"、4.07 → "4:02"、75 → "1:15:00"。
 */
export function formatDurationLabel(sec: number, fps: number): string {
  if (!Number.isFinite(sec) || !(fps > 0)) return '0:00';
  const sign = sec < 0 ? '-' : '';
  return `${sign}${framesToDurationLabel(secToFrames(Math.abs(sec), fps), fps)}`;
}

/**
 * 「これ以上必要」の下限 (秒) を「秒:フレーム」で表す。フレーム数は**切り上げ**る
 * (位置はフレームの目盛りに乗るので、0.1 秒の下限は 24fps では 3 フレーム目から満たす → "0:03")。
 */
export function formatMinDurationLabel(sec: number, fps: number): string {
  if (!Number.isFinite(sec) || !(fps > 0)) return '0:00';
  return framesToDurationLabel(Math.max(0, Math.ceil(sec * fps - 1e-6)), fps);
}

/** 動画全体の総尺の表示 (右ドックの情報エリア ProjectInfo)。"32.4秒" / 60 秒以上は "1分05.2秒"。0.1 秒単位。
 *  フレーム表記 ("S:FF") だと分:秒と読み違えやすいので、ここだけ秒の小数で出す。 */
export function formatTotalDuration(sec: number): string {
  const tenths = Math.round(Math.max(0, sec) * 10);
  const minutes = Math.floor(tenths / 600);
  const restSec = (tenths % 600) / 10;
  if (minutes === 0) return `${restSec.toFixed(1)}秒`;
  return `${minutes}分${restSec.toFixed(1).padStart(4, '0')}秒`;
}
